"use strict";
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.resultRowDraftServiceFactory = exports.ResultRowDraftServiceImpl = exports.classifyDraftFeedbackTone = void 0;
var resultRowDraftStorage_1 = require("./resultRowDraftStorage");
var editWorkingStream_1 = require("./editWorkingStream");
var editTypingStream_1 = require("./editTypingStream");
var localDraftInventory_1 = require("./localDraftInventory");
function classifyDraftFeedbackTone(message) {
    var normalized = (message || '').trim().toLowerCase();
    if (!normalized) {
        return 'neutral';
    }
    if (normalized.includes('confirmation') || normalized.includes('see warning')) {
        return 'warning';
    }
    if (normalized.includes('nothing to undo') || normalized.includes('no local draft')) {
        return 'neutral';
    }
    if (normalized.includes('already matches')) {
        return 'neutral';
    }
    if (normalized.includes('restored') ||
        normalized.includes('recovered') ||
        normalized.includes('moved to archive') ||
        normalized.includes('previous form state')) {
        return 'success';
    }
    if (normalized.includes('discarded')) {
        return 'neutral';
    }
    if (normalized.includes('below') || normalized.includes('review') || normalized.includes('use changes')) {
        return 'info';
    }
    return 'neutral';
}
exports.classifyDraftFeedbackTone = classifyDraftFeedbackTone;
function getTarget(row, mode) {
    var target = mode === 'edit' ? row === null || row === void 0 ? void 0 : row.edit : row === null || row === void 0 ? void 0 : row.reply;
    return target !== null && target !== void 0 ? target : null;
}
function sessionKey(rowId, mode) {
    return "".concat(rowId, ":").concat(mode);
}
var ResultRowDraftServiceImpl = /** @class */ (function () {
    function ResultRowDraftServiceImpl($timeout, getStorage) {
        this.$timeout = $timeout;
        this.getStorage = getStorage;
        this.sessions = new Map();
        this.lifecycleBound = false;
    }
    ResultRowDraftServiceImpl.prototype.open = function ($scope, row, mode, personId) {
        var target = getTarget(row, mode);
        if (!target || (row === null || row === void 0 ? void 0 : row.ID) === undefined) {
            return null;
        }
        // Persist any previous session for this id (may be another row object) then drop it.
        // Callers that reload from server should have already sealed via sealInProgressEdit;
        // close() is still safe (no-op if sealed).
        this.close(row, mode);
        this.ensureLifecycleHooks();
        var key = sessionKey(row.ID, mode);
        var baselineFields = (0, resultRowDraftStorage_1.collectDraftFields)(target, mode);
        var formBody = target.pnBody == null ? '' : String(target.pnBody);
        var session = {
            mode: mode,
            rowId: row.ID,
            personId: personId,
            rowRef: row,
            baselineFields: baselineFields,
            baselineChecksum: (0, resultRowDraftStorage_1.draftChecksum)(target, mode),
            lastPersistedChecksum: null,
            // Server form is authority until the user types again — blocks laggy outline
            // $destroy from a previous instance writing old body onto this form.
            protectFormBody: formBody,
        };
        this.sessions.set(key, session);
        // Compact indicators only — never auto-open full diff or archive list on edit open.
        target._draftDiffOpen = false;
        target._draftArchivedVisible = false;
        this.refreshUi(row, mode, personId, session);
        this.attachWatcher($scope, row, mode, personId, session);
        // Working stream (always-on local copy) — strongest recovery signal.
        var working = (0, editWorkingStream_1.readEditWorkingStream)(this.getStorage(), personId, mode, row.ID);
        if (working && (0, editWorkingStream_1.workingDiffersFromBody)(working, formBody)) {
            target._workingStreamAt = working.updatedAt;
            target._workingStreamSource = working.source;
            target._workingStreamAvailable = true;
            return "Local working copy from ".concat(new Date(working.updatedAt).toLocaleString(), " \u2014 use restore to continue brainstorming (not saved to server until you Save).");
        }
        target._workingStreamAvailable = false;
        var ui = this.readUi(target);
        if (!ui.revisionCount && !ui.archivedCount) {
            return null;
        }
        // Toast/message only; toolbar shows draft/restore dots. Diff stays collapsed.
        if (ui.revisionCount > 0) {
            if (ui.changeItems.length || ui.changedFields) {
                return 'Local draft with unsaved changes — use restore, or changes to review diff.';
            }
            return 'Local draft available — use restore to apply.';
        }
        if (ui.archivedCount > 0) {
            return "Archived draft(s) available (".concat(ui.archivedCount, ") \u2014 open archived if needed.");
        }
        return null;
    };
    ResultRowDraftServiceImpl.prototype.sealInProgressEdit = function (rowId, personId, mode) {
        var _a;
        if (mode === void 0) { mode = 'edit'; }
        if (rowId === undefined || rowId === null || rowId === '') {
            return;
        }
        var session = this.sessions.get(sessionKey(rowId, mode));
        if (!session || !session.rowRef) {
            return;
        }
        if (session.timerId) {
            this.$timeout.cancel(session.timerId);
            session.timerId = undefined;
        }
        try {
            this.persistIfEdited(session.rowRef, session.mode, personId !== null && personId !== void 0 ? personId : session.personId, session);
        }
        catch (_b) {
            /* ignore */
        }
        try {
            (_a = session.unwatch) === null || _a === void 0 ? void 0 : _a.call(session);
        }
        catch (_c) {
            /* ignore */
        }
        this.sessions.delete(sessionKey(session.rowId, session.mode));
    };
    ResultRowDraftServiceImpl.prototype.close = function (row, mode) {
        var _a;
        var session = this.findSession(row, mode);
        if (!session) {
            return;
        }
        // Last-chance persist before tearing down the watcher (cancel / save & close).
        if (session.timerId) {
            this.$timeout.cancel(session.timerId);
            session.timerId = undefined;
        }
        this.persistIfEdited(session.rowRef || row, mode, session.personId, session);
        (_a = session.unwatch) === null || _a === void 0 ? void 0 : _a.call(session);
        this.sessions.delete(sessionKey(session.rowId, session.mode));
    };
    ResultRowDraftServiceImpl.prototype.flush = function (row, mode, personId) {
        var _a;
        var session = (_a = this.findSession(row, mode)) !== null && _a !== void 0 ? _a : this.ensureSession(row, mode, personId);
        if (!session) {
            return;
        }
        if (session.timerId) {
            this.$timeout.cancel(session.timerId);
            session.timerId = undefined;
        }
        session.rowRef = row;
        this.persistIfEdited(row, mode, personId, session);
    };
    ResultRowDraftServiceImpl.prototype.flushAllOpenSessions = function () {
        var _this = this;
        this.sessions.forEach(function (session) {
            if (!session.rowRef) {
                return;
            }
            if (session.timerId) {
                _this.$timeout.cancel(session.timerId);
                session.timerId = undefined;
            }
            _this.persistIfEdited(session.rowRef, session.mode, session.personId, session);
        });
    };
    ResultRowDraftServiceImpl.prototype.scheduleDebouncedPersistAll = function (rowId) {
        var _this = this;
        this.sessions.forEach(function (session) {
            if (!session.rowRef) {
                return;
            }
            if (rowId !== undefined &&
                rowId !== null &&
                rowId !== '' &&
                String(session.rowId) !== String(rowId)) {
                return;
            }
            if (session.timerId) {
                _this.$timeout.cancel(session.timerId);
            }
            var row = session.rowRef;
            var mode = session.mode;
            var personId = session.personId;
            session.timerId = _this.$timeout(function () {
                _this.persistIfEdited(row, mode, personId, session);
                session.timerId = undefined;
            }, resultRowDraftStorage_1.LOCAL_DRAFT_DEBOUNCE_MS);
        });
    };
    ResultRowDraftServiceImpl.prototype.applyLiveBodyToOpenEditSessions = function (body, source, rowId) {
        if (source === void 0) { source = 'unknown'; }
        var next = body == null ? '' : String(body);
        var storage = this.getStorage();
        var typingSource = (source === 'unknown' ? 'type' : source);
        var immediate = source === 'emergency' || source === 'map' || source === 'blur';
        var targets = this.editSessionsForBodyApply(rowId);
        targets.forEach(function (session) {
            var _a;
            if (!((_a = session.rowRef) === null || _a === void 0 ? void 0 : _a.edit)) {
                return;
            }
            // After server reopen: ignore laggy destroy/emergency from a prior outline instance.
            // Do not rewrite working stream (WIP was sealed before reload).
            if (session.protectFormBody !== undefined &&
                next !== session.protectFormBody) {
                if (source === 'type' || source === 'map') {
                    session.protectFormBody = undefined;
                }
                else {
                    return;
                }
            }
            else if (source === 'type' || source === 'map') {
                session.protectFormBody = undefined;
            }
            // After restore/undo, laggy emergency/flush/unknown must not re-write the old form
            // as the durable working copy (that made restored content fragile).
            if (session.restoreAuthorityBody !== undefined &&
                next !== session.restoreAuthorityBody) {
                if (source === 'type' || source === 'map') {
                    session.restoreAuthorityBody = undefined;
                }
                else {
                    // Re-assert sealed body on parent form; ignore stomp.
                    session.rowRef.edit.pnBody = session.restoreAuthorityBody;
                    (0, editWorkingStream_1.writeEditWorkingStream)(storage, {
                        personId: session.personId,
                        mode: session.mode,
                        rowId: session.rowId,
                        body: session.restoreAuthorityBody,
                        source: 'restore',
                        forceCheckpoint: false,
                    });
                    return;
                }
            }
            session.rowRef.edit.pnBody = next;
            // Always write working stream — do not gate on baseline/dirty heuristics.
            (0, editWorkingStream_1.writeEditWorkingStream)(storage, {
                personId: session.personId,
                mode: session.mode,
                rowId: session.rowId,
                body: next,
                source: source,
                forceCheckpoint: source === 'emergency' || source === 'map',
            });
            // Continuous typing / change ring (silent; not a draft pill).
            (0, editTypingStream_1.recordTypingBody)(storage, {
                personId: session.personId,
                mode: session.mode,
                rowId: session.rowId,
                body: next,
                source: typingSource,
                immediate: immediate,
            });
        });
    };
    ResultRowDraftServiceImpl.prototype.closeOtherEditSessions = function (personId, keepRowId) {
        var _this = this;
        var toClose = [];
        this.sessions.forEach(function (session) {
            if (session.mode !== 'edit')
                return;
            if (keepRowId !== undefined &&
                keepRowId !== null &&
                String(session.rowId) === String(keepRowId)) {
                return;
            }
            toClose.push(session);
        });
        toClose.forEach(function (session) {
            var _a;
            var row = session.rowRef;
            if (session.timerId) {
                _this.$timeout.cancel(session.timerId);
                session.timerId = undefined;
            }
            try {
                _this.persistIfEdited(row, 'edit', personId !== null && personId !== void 0 ? personId : session.personId, session);
            }
            catch (_b) {
                /* ignore */
            }
            try {
                (_a = session.unwatch) === null || _a === void 0 ? void 0 : _a.call(session);
            }
            catch (_c) {
                /* ignore */
            }
            _this.sessions.delete(sessionKey(session.rowId, session.mode));
            try {
                if (row && row.edit) {
                    row.edit = null;
                }
            }
            catch (_d) {
                /* ignore */
            }
        });
    };
    ResultRowDraftServiceImpl.prototype.recordTypingForOpenSessions = function (body, source, immediate, rowId) {
        if (source === void 0) { source = 'type'; }
        if (immediate === void 0) { immediate = false; }
        var next = body == null ? '' : String(body);
        var storage = this.getStorage();
        var targets = this.editSessionsForBodyApply(rowId);
        targets.forEach(function (session) {
            var _a;
            if (!((_a = session.rowRef) === null || _a === void 0 ? void 0 : _a.edit)) {
                return;
            }
            session.rowRef.edit.pnBody = next;
            (0, editTypingStream_1.recordTypingBody)(storage, {
                personId: session.personId,
                mode: session.mode,
                rowId: session.rowId,
                body: next,
                source: source,
                immediate: immediate,
            });
            (0, editWorkingStream_1.writeEditWorkingStream)(storage, {
                personId: session.personId,
                mode: session.mode,
                rowId: session.rowId,
                body: next,
                source: source === 'type' || source === 'paste' ? 'type' : source,
            });
        });
    };
    ResultRowDraftServiceImpl.prototype.flushOpenEditSession = function (rowId) {
        var _this = this;
        if (rowId === undefined || rowId === null || rowId === '') {
            return;
        }
        this.sessions.forEach(function (session) {
            var _a;
            if (session.mode !== 'edit' || !((_a = session.rowRef) === null || _a === void 0 ? void 0 : _a.edit)) {
                return;
            }
            if (String(session.rowId) !== String(rowId)) {
                return;
            }
            if (session.timerId) {
                _this.$timeout.cancel(session.timerId);
                session.timerId = undefined;
            }
            _this.persistIfEdited(session.rowRef, session.mode, session.personId, session);
        });
    };
    /**
     * Sessions allowed to receive a live body/typing apply.
     * With rowId: only that edit session.
     * Without rowId: only when exactly one edit session is open (never multi-row fan-out).
     */
    ResultRowDraftServiceImpl.prototype.editSessionsForBodyApply = function (rowId) {
        var editSessions = [];
        this.sessions.forEach(function (session) {
            var _a;
            if (session.mode === 'edit' && ((_a = session.rowRef) === null || _a === void 0 ? void 0 : _a.edit)) {
                editSessions.push(session);
            }
        });
        if (rowId !== undefined && rowId !== null && rowId !== '') {
            return editSessions.filter(function (s) { return String(s.rowId) === String(rowId); });
        }
        // Missing rowId is only safe for a single open editor.
        return editSessions.length === 1 ? editSessions : [];
    };
    ResultRowDraftServiceImpl.prototype.flushTypingForOpenSessions = function () {
        var storage = this.getStorage();
        (0, editTypingStream_1.flushTypingStreamPending)(storage);
        this.sessions.forEach(function (session) {
            if (session.mode !== 'edit')
                return;
            (0, editTypingStream_1.flushTypingStreamPending)(storage, session.personId, session.mode, session.rowId);
        });
    };
    ResultRowDraftServiceImpl.prototype.getWorkingStream = function (personId, mode, rowId) {
        return (0, editWorkingStream_1.readEditWorkingStream)(this.getStorage(), personId, mode, rowId);
    };
    ResultRowDraftServiceImpl.prototype.listLocalInventory = function (personId) {
        // Push throttled typing ticks + working body from open editors first so the
        // profile list matches the form (draft tip alone is often behind keystrokes).
        try {
            this.flushTypingForOpenSessions();
        }
        catch (_a) {
            /* ignore */
        }
        var storage = this.getStorage();
        var live = [];
        this.sessions.forEach(function (session) {
            if (!session.rowRef)
                return;
            var target = getTarget(session.rowRef, session.mode);
            if (!target)
                return;
            var body = target.pnBody == null ? '' : String(target.pnBody);
            var now = new Date().toISOString();
            live.push({
                personId: personId !== null && personId !== void 0 ? personId : session.personId,
                mode: session.mode,
                rowId: session.rowId,
                body: body,
                updatedAt: now,
            });
            // Seal working to live form so inventory remains correct after edit closes.
            if (storage && session.mode === 'edit') {
                (0, editWorkingStream_1.writeEditWorkingStream)(storage, {
                    personId: personId !== null && personId !== void 0 ? personId : session.personId,
                    mode: session.mode,
                    rowId: session.rowId,
                    body: body,
                    fields: (0, resultRowDraftStorage_1.collectDraftFields)(target, session.mode),
                    source: 'flush',
                });
            }
        });
        return (0, localDraftInventory_1.scanLocalDraftInventory)(storage, personId, live);
    };
    ResultRowDraftServiceImpl.prototype.hasUnsavedOpenEdits = function () {
        var unsaved = false;
        this.sessions.forEach(function (session) {
            if (unsaved || !session.rowRef)
                return;
            var target = getTarget(session.rowRef, session.mode);
            if (!target)
                return;
            var fields = (0, resultRowDraftStorage_1.collectDraftFields)(target, session.mode);
            if ((0, resultRowDraftStorage_1.hasEditsFromBaseline)(session.baselineFields, fields, session.mode)) {
                unsaved = true;
            }
        });
        return unsaved;
    };
    ResultRowDraftServiceImpl.prototype.ensureLifecycleHooks = function () {
        var _this = this;
        if (this.lifecycleBound || typeof window === 'undefined') {
            return;
        }
        this.lifecycleBound = true;
        // Sync localStorage — must not wait on Angular digests (Android Back / tab kill).
        var emergencyFlush = function () {
            try {
                _this.flushTypingForOpenSessions();
                _this.flushAllOpenSessions();
            }
            catch (_a) {
                /* ignore storage errors during unload */
            }
        };
        window.addEventListener('beforeunload', emergencyFlush);
        window.addEventListener('pagehide', emergencyFlush);
        // iOS/Android tab switch / app background
        document.addEventListener('visibilitychange', function () {
            if (document.visibilityState === 'hidden') {
                emergencyFlush();
            }
        });
        // Page Lifecycle API (Chrome Android)
        try {
            document.addEventListener('freeze', emergencyFlush);
        }
        catch (_a) {
            /* ignore */
        }
        // Browser Back often fires popstate before unload — persist first.
        window.addEventListener('popstate', emergencyFlush);
    };
    ResultRowDraftServiceImpl.prototype.clear = function (row, mode, personId, reason) {
        var _a, _b;
        if (reason === void 0) { reason = 'cleared'; }
        var storage = this.getStorage();
        var target = getTarget(row, mode);
        if (!storage || !target) {
            return;
        }
        try {
            (0, resultRowDraftStorage_1.archiveAndClearDraftHistory)(storage, personId, mode, row === null || row === void 0 ? void 0 : row.ID, reason);
            (0, editWorkingStream_1.clearEditWorkingStream)(storage, personId, mode, row === null || row === void 0 ? void 0 : row.ID);
            (0, editTypingStream_1.clearTypingStream)(storage, personId, mode, row === null || row === void 0 ? void 0 : row.ID);
            target._workingStreamAvailable = false;
            var session = this.findSession(row, mode);
            if (session) {
                session.undoSnapshot = undefined;
                session.restoreAuthorityBody = undefined;
                session.skipPersistOnce = false;
            }
            this.syncArchivedUi(row, mode, personId);
            if (session) {
                this.refreshUi(row, mode, personId, session);
            }
            else {
                this.applyUi(target, __assign(__assign({}, this.emptyUi()), { archivedCount: (_a = target._archivedDraftCount) !== null && _a !== void 0 ? _a : 0, archivedItems: (_b = target._draftArchivedItems) !== null && _b !== void 0 ? _b : [] }));
            }
        }
        catch (_c) {
            /* ignore quota / private mode */
        }
    };
    ResultRowDraftServiceImpl.prototype.clearAll = function (row, personId, reason) {
        if (reason === void 0) { reason = 'cleared'; }
        this.clear(row, 'edit', personId, reason);
        this.clear(row, 'reply', personId, reason);
    };
    ResultRowDraftServiceImpl.prototype.acknowledgeServerSave = function (row, mode, personId) {
        var session = this.findSession(row, mode);
        var target = getTarget(row, mode);
        if (!session || !target) {
            return;
        }
        session.baselineFields = (0, resultRowDraftStorage_1.collectDraftFields)(target, mode);
        session.baselineChecksum = (0, resultRowDraftStorage_1.draftChecksum)(target, mode);
        session.lastPersistedChecksum = null;
        session.undoSnapshot = undefined;
        session.skipPersistOnce = false;
        session.restoreAuthorityBody = undefined;
        target._draftArchivedVisible = false;
        target._draftRestoreWarning = null;
        target._draftClearWarning = null;
        // DB commit succeeded — drop disposable local working + typing streams.
        (0, editWorkingStream_1.clearEditWorkingStream)(this.getStorage(), personId, mode, row === null || row === void 0 ? void 0 : row.ID);
        (0, editTypingStream_1.clearTypingStream)(this.getStorage(), personId, mode, row === null || row === void 0 ? void 0 : row.ID);
        target._workingStreamAvailable = false;
        this.refreshUi(row, mode, personId, session);
    };
    ResultRowDraftServiceImpl.prototype.listArchived = function (row, mode, personId) {
        var storage = this.getStorage();
        if (!storage || (row === null || row === void 0 ? void 0 : row.ID) === undefined) {
            return [];
        }
        return (0, resultRowDraftStorage_1.listArchivedDraftSummaries)(storage, personId, { rowId: row.ID, mode: mode });
    };
    ResultRowDraftServiceImpl.prototype.showArchived = function (row, mode, personId) {
        var target = getTarget(row, mode);
        if (!target) {
            return [];
        }
        var storage = this.getStorage();
        if (!storage || (row === null || row === void 0 ? void 0 : row.ID) === undefined) {
            return [];
        }
        // Pass open form so each archive can show vs-form + lineage diffs
        var items = (0, resultRowDraftStorage_1.listArchivedDraftSummaries)(storage, personId, { rowId: row.ID, mode: mode }, target);
        target._draftArchivedItems = items;
        target._archivedDraftCount = items.length;
        target._draftArchivedVisible = true;
        return items;
    };
    ResultRowDraftServiceImpl.prototype.restoreArchived = function (row, mode, personId, archiveId) {
        var storage = this.getStorage();
        var target = getTarget(row, mode);
        if (!storage || !target || (row === null || row === void 0 ? void 0 : row.ID) === undefined) {
            return 'Open edit/reply first, then recover archived draft.';
        }
        var archived = (0, resultRowDraftStorage_1.findArchivedDraftRecord)(storage, personId, archiveId);
        if (!archived) {
            return 'Archived draft not found.';
        }
        if (String(archived.rowId) !== String(row.ID) || archived.mode !== mode) {
            return 'Archived draft does not match this row.';
        }
        var applyIndex = (0, resultRowDraftStorage_1.pickArchivePreviewRevisionIndex)(archived.history);
        var recoverFields = (0, resultRowDraftStorage_1.getRecoverRevisionFields)(archived.history);
        if ((0, resultRowDraftStorage_1.formMatchesRevisionFields)(target, mode, recoverFields)) {
            return 'Form already matches this archived draft — no recover needed.';
        }
        var currentFields = (0, resultRowDraftStorage_1.collectDraftFields)(target, mode);
        var backedUpCurrent = (0, resultRowDraftStorage_1.archiveCurrentFormBeforeRecover)(storage, personId, mode, row.ID, currentFields, recoverFields);
        var history = (0, resultRowDraftStorage_1.restoreArchivedDraftToActive)(storage, personId, archiveId);
        if (!history) {
            return 'Archived draft not found.';
        }
        if (!this.applyRevision(row, mode, personId, applyIndex)) {
            return 'Failed to apply recovered draft.';
        }
        this.syncArchivedUi(row, mode, personId);
        if (backedUpCurrent) {
            return "Recovered archived draft (".concat(history.revisions.length, " revision(s)). Your previous form content was archived as \"replaced\". Use Undo or Archived to switch back.");
        }
        return "Recovered archived draft (".concat(history.revisions.length, " revision(s)). Use Undo if needed.");
    };
    ResultRowDraftServiceImpl.prototype.discardArchived = function (row, personId, archiveId) {
        var storage = this.getStorage();
        if (!storage) {
            return 'Storage unavailable.';
        }
        if (!(0, resultRowDraftStorage_1.discardArchivedDraft)(storage, personId, archiveId)) {
            return 'Archived draft not found.';
        }
        if (row === null || row === void 0 ? void 0 : row.edit) {
            this.syncArchivedUi(row, 'edit', personId);
        }
        if (row === null || row === void 0 ? void 0 : row.reply) {
            this.syncArchivedUi(row, 'reply', personId);
        }
        return 'Archived draft discarded.';
    };
    ResultRowDraftServiceImpl.prototype.preview = function (row, mode, personId) {
        var _a;
        var target = getTarget(row, mode);
        if (!target) {
            return 'Open edit/reply first, then preview draft.';
        }
        var session = (_a = this.findSession(row, mode)) !== null && _a !== void 0 ? _a : this.ensureSession(row, mode, personId);
        if (session) {
            this.refreshUi(row, mode, personId, session);
        }
        var ui = this.readUi(target);
        if (!ui.revisionCount) {
            if (ui.archivedCount > 0) {
                // User asked for changes — point at archives without forcing panel open
                // (click "archived" to list).
                return "No active draft \u2014 ".concat(ui.archivedCount, " archived draft(s); open archived to list.");
            }
            return 'No local draft found.';
        }
        // Explicit "changes" click: reveal diff panel.
        if (ui.changeItems.length) {
            target._draftDiffOpen = true;
            return ui.previewSummary || 'Draft changes shown below.';
        }
        return "Local draft present (".concat(ui.revisionCount, " revision(s), saved ").concat(ui.savedAt ? new Date(ui.savedAt).toLocaleString() : 'recently', "). Use restore if the form does not match.");
    };
    ResultRowDraftServiceImpl.prototype.restore = function (row, mode, personId) {
        var target = getTarget(row, mode);
        var storage = this.getStorage();
        if (!target || !storage) {
            return 'Open edit/reply first, then restore draft.';
        }
        // Prefer working stream (always-on local copy) when it differs from the form.
        var working = (0, editWorkingStream_1.readEditWorkingStream)(storage, personId, mode, row === null || row === void 0 ? void 0 : row.ID);
        var formBody = target.pnBody == null ? '' : String(target.pnBody);
        if (working && (0, editWorkingStream_1.workingDiffersFromBody)(working, formBody)) {
            return this.applyWorkingStream(row, mode, personId, working);
        }
        var history = (0, resultRowDraftStorage_1.loadDraftHistory)(storage, personId, mode, row === null || row === void 0 ? void 0 : row.ID);
        if (!(history === null || history === void 0 ? void 0 : history.revisions.length)) {
            return 'No local draft found.';
        }
        // Restore always means *latest* tip — not an older revision under a matching tip
        // (that used to re-apply the pre-restore form and make the real draft fragile).
        var latest = history.revisions[0];
        if ((0, resultRowDraftStorage_1.formMatchesRevisionFields)(target, mode, latest.fields)) {
            return 'Form already matches local draft.';
        }
        var preview = (0, resultRowDraftStorage_1.buildRestorePreview)(history, target, 0);
        if (!preview) {
            return 'No local draft found.';
        }
        if (preview.hasRiskyChanges) {
            var warning = "".concat(preview.summaryText, " Restore anyway? Use Undo immediately after if needed.");
            target._draftRestoreWarning = warning;
            target._draftClearWarning = null;
            return 'Draft restore needs confirmation — see warning below.';
        }
        return this.applyRevision(row, mode, personId, 0)
            ? "Local draft restored (".concat(preview.revisionCount, " revision(s) in history).")
            : 'No local draft found.';
    };
    ResultRowDraftServiceImpl.prototype.confirmRestore = function (row, mode, personId) {
        var target = getTarget(row, mode);
        var storage = this.getStorage();
        if (target && storage && (row === null || row === void 0 ? void 0 : row.ID) !== undefined) {
            var working = (0, editWorkingStream_1.readEditWorkingStream)(storage, personId, mode, row.ID);
            var formBody = target.pnBody == null ? '' : String(target.pnBody);
            if (working && (0, editWorkingStream_1.workingDiffersFromBody)(working, formBody)) {
                return this.applyWorkingStream(row, mode, personId, working);
            }
            var history_1 = (0, resultRowDraftStorage_1.loadDraftHistory)(storage, personId, mode, row.ID);
            if ((history_1 === null || history_1 === void 0 ? void 0 : history_1.revisions[0]) && (0, resultRowDraftStorage_1.formMatchesRevisionFields)(target, mode, history_1.revisions[0].fields)) {
                return 'Form already matches local draft.';
            }
        }
        return this.applyRevision(row, mode, personId, 0)
            ? 'Local draft restored. Use Undo if this overwrote something important.'
            : 'No local draft found.';
    };
    /** Apply durable working stream into the open form (continue brainstorm; DB still separate). */
    ResultRowDraftServiceImpl.prototype.applyWorkingStream = function (row, mode, personId, working) {
        var _a;
        var target = getTarget(row, mode);
        if (!target) {
            return 'Open edit first.';
        }
        var session = (_a = this.findSession(row, mode)) !== null && _a !== void 0 ? _a : this.beginSession(row, mode, personId, target);
        if (session.timerId) {
            this.$timeout.cancel(session.timerId);
            session.timerId = undefined;
        }
        // Fields from working snapshot when present; body always from working.body.
        var restoredFields = working.fields && typeof working.fields === 'object'
            ? __assign(__assign({}, working.fields), { pnBody: working.body == null ? '' : String(working.body) }) : __assign(__assign({}, (0, resultRowDraftStorage_1.collectDraftFields)(target, mode)), { pnBody: working.body == null ? '' : String(working.body) });
        this.sealProgrammaticRestore(row, mode, personId, session, target, restoredFields);
        return "Working copy restored (".concat(new Date(working.updatedAt).toLocaleString(), ", ").concat(working.source, "). Continue editing \u2014 Save when ready to commit to the server.");
    };
    ResultRowDraftServiceImpl.prototype.undo = function (row, mode, personId) {
        var target = getTarget(row, mode);
        var session = this.findSession(row, mode);
        var snapshot = session === null || session === void 0 ? void 0 : session.undoSnapshot;
        if (!target || !session || !snapshot) {
            return 'Nothing to undo.';
        }
        if (session.timerId) {
            this.$timeout.cancel(session.timerId);
            session.timerId = undefined;
        }
        (0, resultRowDraftStorage_1.applyUndoSnapshot)(target, snapshot, mode);
        // Fresh body string for isolate/ngModel.
        if (Object.prototype.hasOwnProperty.call(snapshot.fields, 'pnBody')) {
            var body = snapshot.fields.pnBody;
            target.pnBody = body == null ? '' : String(body);
        }
        var undidBody = target.pnBody == null ? '' : String(target.pnBody);
        session.undoSnapshot = undefined;
        // Do not promote undid form over the restored draft tip — keep tip so Restore works again.
        // Only seal working + authority so lag cannot re-stomp the undid form either.
        session.restoreAuthorityBody = undidBody;
        session.skipPersistOnce = true;
        session.lastPersistedChecksum = (0, resultRowDraftStorage_1.draftChecksum)(target, mode);
        var storage = this.getStorage();
        if (storage) {
            (0, editWorkingStream_1.writeEditWorkingStream)(storage, {
                personId: personId,
                mode: mode,
                rowId: row.ID,
                body: undidBody,
                fields: (0, resultRowDraftStorage_1.collectDraftFields)(target, mode),
                source: 'restore',
                forceCheckpoint: true,
            });
        }
        this.refreshUi(row, mode, personId, session);
        return 'Restored previous form state.';
    };
    /**
     * Seal restored (or undid) form state as the durable local tip.
     * Pre-restore form goes to undo only (and a working checkpoint), never left as the
     * active draft tip over the content the user just recovered.
     */
    ResultRowDraftServiceImpl.prototype.sealProgrammaticRestore = function (row, mode, personId, session, target, restoredFields, opts) {
        var storage = this.getStorage();
        var keepUndo = (opts === null || opts === void 0 ? void 0 : opts.keepUndo) !== false;
        var source = (opts === null || opts === void 0 ? void 0 : opts.source) || 'restore';
        var preFields = (0, resultRowDraftStorage_1.collectDraftFields)(target, mode);
        var preBody = target.pnBody == null ? '' : String(target.pnBody);
        var restoredBody = restoredFields.pnBody == null ? '' : String(restoredFields.pnBody);
        // 1) Checkpoint pre-restore form into working ring (recoverable) without making it tip later.
        if (storage && preBody !== restoredBody) {
            (0, editWorkingStream_1.writeEditWorkingStream)(storage, {
                personId: personId,
                mode: mode,
                rowId: row.ID,
                body: preBody,
                fields: preFields,
                source: 'flush',
                forceCheckpoint: true,
            });
        }
        // 2) If pre-form was a real unsaved edit, keep it in revision history *under* the tip.
        if (storage &&
            (0, resultRowDraftStorage_1.hasEditsFromBaseline)(session.baselineFields, preFields, mode) &&
            !(0, resultRowDraftStorage_1.revisionsEqual)(preFields, restoredFields, mode)) {
            try {
                var existing = (0, resultRowDraftStorage_1.loadDraftHistory)(storage, personId, mode, row.ID);
                var withPre = (0, resultRowDraftStorage_1.appendDraftRevision)(existing !== null && existing !== void 0 ? existing : {
                    mode: mode,
                    rowId: row.ID,
                    personId: personId,
                    revisions: [],
                }, preFields);
                if (!existing || withPre !== existing) {
                    (0, resultRowDraftStorage_1.saveDraftHistory)(storage, withPre);
                }
            }
            catch (_a) {
                /* ignore */
            }
        }
        if (keepUndo) {
            session.undoSnapshot = (0, resultRowDraftStorage_1.captureUndoSnapshot)(target, mode, this.readUi(target).savedAt);
        }
        // 3) Apply restored fields onto form.
        (0, resultRowDraftStorage_1.applyDraftFields)(target, restoredFields);
        target.pnBody = restoredBody;
        // 4) Durable tip = restored content (working + draft revision + authority guard).
        session.restoreAuthorityBody = restoredBody;
        session.skipPersistOnce = true;
        session.lastPersistedChecksum = (0, resultRowDraftStorage_1.draftChecksum)(target, mode);
        if (storage) {
            (0, editWorkingStream_1.writeEditWorkingStream)(storage, {
                personId: personId,
                mode: mode,
                rowId: row.ID,
                body: restoredBody,
                fields: (0, resultRowDraftStorage_1.collectDraftFields)(target, mode),
                source: source,
                forceCheckpoint: true,
            });
            (0, editTypingStream_1.recordTypingBody)(storage, {
                personId: personId,
                mode: mode,
                rowId: row.ID,
                body: restoredBody,
                source: 'flush',
                immediate: true,
            });
            try {
                if ((0, resultRowDraftStorage_1.hasEditsFromBaseline)(session.baselineFields, (0, resultRowDraftStorage_1.collectDraftFields)(target, mode), mode)) {
                    var existing = (0, resultRowDraftStorage_1.loadDraftHistory)(storage, personId, mode, row.ID);
                    var withRestored = (0, resultRowDraftStorage_1.appendDraftRevision)(existing !== null && existing !== void 0 ? existing : {
                        mode: mode,
                        rowId: row.ID,
                        personId: personId,
                        revisions: [],
                    }, (0, resultRowDraftStorage_1.collectDraftFields)(target, mode));
                    if (!existing || withRestored !== existing) {
                        (0, resultRowDraftStorage_1.saveDraftHistory)(storage, withRestored);
                    }
                }
            }
            catch (_b) {
                /* ignore */
            }
        }
        target._workingStreamAvailable = false;
        target._draftRestoreWarning = null;
        this.refreshUi(row, mode, personId, session);
    };
    ResultRowDraftServiceImpl.prototype.requestClear = function (row, mode, personId) {
        var target = getTarget(row, mode);
        var storage = this.getStorage();
        if (!target || !storage) {
            return 'Open edit/reply first, then clear draft.';
        }
        var history = (0, resultRowDraftStorage_1.loadDraftHistory)(storage, personId, mode, row === null || row === void 0 ? void 0 : row.ID);
        if (!(history === null || history === void 0 ? void 0 : history.revisions.length)) {
            target._draftClearWarning = null;
            return 'No local draft found.';
        }
        var preview = (0, resultRowDraftStorage_1.buildRestorePreview)(history, target);
        var summary = (preview === null || preview === void 0 ? void 0 : preview.summaryText) ? " ".concat(preview.summaryText) : '';
        var warning = "Archive ".concat(history.revisions.length, " local draft revision(s)?").concat(summary, " Cleared drafts move to Archived (kept ").concat(resultRowDraftStorage_1.LOCAL_DRAFT_RETENTION_DAYS, " days) and can be recovered.");
        target._draftClearWarning = warning;
        target._draftRestoreWarning = null;
        return 'Draft clear needs confirmation — see warning below.';
    };
    ResultRowDraftServiceImpl.prototype.confirmClear = function (row, mode, personId) {
        var target = getTarget(row, mode);
        if (!target) {
            return 'Open edit/reply first, then clear draft.';
        }
        this.clear(row, mode, personId);
        target._draftClearWarning = null;
        return 'Local draft moved to archive — use Archived to recover.';
    };
    ResultRowDraftServiceImpl.prototype.findSession = function (row, mode) {
        if ((row === null || row === void 0 ? void 0 : row.ID) === undefined) {
            return undefined;
        }
        return this.sessions.get(sessionKey(row.ID, mode));
    };
    ResultRowDraftServiceImpl.prototype.ensureSession = function (row, mode, personId) {
        var target = getTarget(row, mode);
        if (!target || (row === null || row === void 0 ? void 0 : row.ID) === undefined) {
            return null;
        }
        var existing = this.findSession(row, mode);
        if (existing) {
            existing.rowRef = row;
            return existing;
        }
        this.ensureLifecycleHooks();
        var baselineFields = (0, resultRowDraftStorage_1.collectDraftFields)(target, mode);
        var session = {
            mode: mode,
            rowId: row.ID,
            personId: personId,
            rowRef: row,
            baselineFields: baselineFields,
            baselineChecksum: (0, resultRowDraftStorage_1.draftChecksum)(target, mode),
            lastPersistedChecksum: null,
        };
        this.sessions.set(sessionKey(row.ID, mode), session);
        return session;
    };
    ResultRowDraftServiceImpl.prototype.attachWatcher = function ($scope, row, mode, personId, session) {
        var _this = this;
        // Watch checksum + pnBody string so isolate-scope outline typing is less likely
        // to miss a digest (body binding can lag a parent-only checksum watch).
        session.unwatch = $scope.$watch(function () {
            var target = getTarget(row, mode);
            if (!target) {
                return null;
            }
            var body = target.pnBody == null ? '' : String(target.pnBody);
            return "".concat((0, resultRowDraftStorage_1.draftChecksum)(target, mode), "\n").concat(body.length, ":").concat(body.slice(0, 64));
        }, function (token) {
            if (!token) {
                return;
            }
            var target = getTarget(row, mode);
            if (!target) {
                return;
            }
            var checksum = (0, resultRowDraftStorage_1.draftChecksum)(target, mode);
            if (checksum === session.baselineChecksum) {
                return;
            }
            if (session.timerId) {
                _this.$timeout.cancel(session.timerId);
            }
            session.timerId = _this.$timeout(function () {
                _this.persistIfEdited(row, mode, personId, session);
                session.timerId = undefined;
            }, resultRowDraftStorage_1.LOCAL_DRAFT_DEBOUNCE_MS);
        });
    };
    ResultRowDraftServiceImpl.prototype.persistIfEdited = function (row, mode, personId, session) {
        var storage = this.getStorage();
        var target = getTarget(row, mode);
        if (!storage || !target) {
            return false;
        }
        var body = target.pnBody == null ? '' : String(target.pnBody);
        // Lag after restore: form may briefly hold pre-restore text. Do not let that
        // become the durable draft tip — re-assert sealed restored body instead.
        if (session.restoreAuthorityBody !== undefined &&
            body !== session.restoreAuthorityBody) {
            target.pnBody = session.restoreAuthorityBody;
            body = session.restoreAuthorityBody;
            (0, editWorkingStream_1.writeEditWorkingStream)(storage, {
                personId: personId,
                mode: mode,
                rowId: row.ID,
                body: body,
                fields: (0, resultRowDraftStorage_1.collectDraftFields)(target, mode),
                source: 'restore',
            });
            session.lastPersistedChecksum = (0, resultRowDraftStorage_1.draftChecksum)(target, mode);
            if (session.skipPersistOnce) {
                session.skipPersistOnce = false;
            }
            return false;
        }
        if (session.skipPersistOnce) {
            session.skipPersistOnce = false;
            session.lastPersistedChecksum = (0, resultRowDraftStorage_1.draftChecksum)(target, mode);
            // Still seal working to restored/current form (revision append skipped once).
            (0, editWorkingStream_1.writeEditWorkingStream)(storage, {
                personId: personId,
                mode: mode,
                rowId: row.ID,
                body: body,
                fields: (0, resultRowDraftStorage_1.collectDraftFields)(target, mode),
                source: 'restore',
            });
            return false;
        }
        var fields = (0, resultRowDraftStorage_1.collectDraftFields)(target, mode);
        var checksum = (0, resultRowDraftStorage_1.draftChecksum)(target, mode);
        // Working stream: always refresh latest body (independent of revision/baseline gates).
        (0, editWorkingStream_1.writeEditWorkingStream)(storage, {
            personId: personId,
            mode: mode,
            rowId: row.ID,
            body: body,
            fields: fields,
            source: 'flush',
        });
        if (!(0, resultRowDraftStorage_1.hasEditsFromBaseline)(session.baselineFields, fields, mode)) {
            this.refreshUi(row, mode, personId, session);
            return false;
        }
        if (checksum === session.lastPersistedChecksum) {
            return false;
        }
        try {
            var existing = (0, resultRowDraftStorage_1.loadDraftHistory)(storage, personId, mode, row === null || row === void 0 ? void 0 : row.ID);
            var history_2 = (0, resultRowDraftStorage_1.appendDraftRevision)(existing !== null && existing !== void 0 ? existing : {
                mode: mode,
                rowId: row.ID,
                personId: personId,
                revisions: [],
            }, fields);
            if (existing && history_2 === existing) {
                this.refreshUi(row, mode, personId, session);
                return false;
            }
            (0, resultRowDraftStorage_1.saveDraftHistory)(storage, history_2);
            session.lastPersistedChecksum = checksum;
            this.refreshUi(row, mode, personId, session);
            return true;
        }
        catch (_a) {
            // Even if revision history fails, working stream was written above.
            return false;
        }
    };
    ResultRowDraftServiceImpl.prototype.applyRevision = function (row, mode, personId, revisionIndex) {
        var _a;
        var storage = this.getStorage();
        var target = getTarget(row, mode);
        if (!storage || !target || (row === null || row === void 0 ? void 0 : row.ID) === undefined) {
            return false;
        }
        var history = (0, resultRowDraftStorage_1.loadDraftHistory)(storage, personId, mode, row.ID);
        if (!(history === null || history === void 0 ? void 0 : history.revisions.length)) {
            return false;
        }
        // Default: latest tip (0). Explicit index used for archived recover.
        // Do not use findRestoreRevisionIndex here — that re-applied older pre-restore
        // forms after a successful restore when the tip already matched the form.
        var index = revisionIndex !== undefined && revisionIndex >= 0 && revisionIndex < history.revisions.length
            ? revisionIndex
            : 0;
        var revision = history.revisions[index];
        if (!revision) {
            return false;
        }
        if ((0, resultRowDraftStorage_1.formMatchesRevisionFields)(target, mode, revision.fields)) {
            return false;
        }
        var session = (_a = this.findSession(row, mode)) !== null && _a !== void 0 ? _a : this.beginSession(row, mode, personId, target);
        if (session.timerId) {
            this.$timeout.cancel(session.timerId);
            session.timerId = undefined;
        }
        // Keep baselineFields/baselineChecksum anchored to the DB state at edit open.
        this.sealProgrammaticRestore(row, mode, personId, session, target, revision.fields);
        return true;
    };
    ResultRowDraftServiceImpl.prototype.beginSession = function (row, mode, personId, target) {
        this.ensureLifecycleHooks();
        var session = {
            mode: mode,
            rowId: row.ID,
            personId: personId,
            rowRef: row,
            baselineFields: (0, resultRowDraftStorage_1.collectDraftFields)(target, mode),
            baselineChecksum: (0, resultRowDraftStorage_1.draftChecksum)(target, mode),
            lastPersistedChecksum: null,
        };
        this.sessions.set(sessionKey(row.ID, mode), session);
        return session;
    };
    ResultRowDraftServiceImpl.prototype.syncArchivedUi = function (row, mode, personId) {
        var target = getTarget(row, mode);
        if (!target || (row === null || row === void 0 ? void 0 : row.ID) === undefined) {
            return;
        }
        var items = this.listArchived(row, mode, personId);
        target._draftArchivedItems = items;
        target._archivedDraftCount = items.length;
    };
    ResultRowDraftServiceImpl.prototype.refreshUi = function (row, mode, personId, session) {
        var _a, _b, _c;
        var target = getTarget(row, mode);
        var storage = this.getStorage();
        if (!target || !storage) {
            return;
        }
        this.syncArchivedUi(row, mode, personId);
        var archivedCount = (_a = target._archivedDraftCount) !== null && _a !== void 0 ? _a : 0;
        var archivedItems = (_b = target._draftArchivedItems) !== null && _b !== void 0 ? _b : [];
        var history = (0, resultRowDraftStorage_1.loadDraftHistory)(storage, personId, mode, row.ID);
        if (!(history === null || history === void 0 ? void 0 : history.revisions.length)) {
            this.applyUi(target, __assign(__assign({}, this.emptyUi()), { archivedCount: archivedCount, archivedItems: archivedItems }));
            return;
        }
        var currentFields = (0, resultRowDraftStorage_1.collectDraftFields)(target, mode);
        // Prefer tip for UI when form already matches latest (do not surface older under-tip revs).
        var tipMatches = (0, resultRowDraftStorage_1.formMatchesRevisionFields)(target, mode, history.revisions[0].fields);
        var restoreIndex = tipMatches ? 0 : (0, resultRowDraftStorage_1.findRestoreRevisionIndex)(history, target);
        var restoreRevision = history.revisions[restoreIndex];
        var editsSinceOpen = (0, resultRowDraftStorage_1.diffSerializedDraftFields)(session.baselineFields, currentFields, mode);
        var changedLabels = editsSinceOpen.map(function (c) { return c.label; });
        var changedDetails = editsSinceOpen.length
            ? editsSinceOpen.map(function (c) { return "".concat(c.label, ": ").concat(c.changeSummary); }).join('; ')
            : null;
        var previewSummary = null;
        var previewTitle = null;
        var changeItems = [];
        var previewRisky = false;
        if (editsSinceOpen.length) {
            previewTitle = 'Unsaved vs server';
            changeItems = (0, resultRowDraftStorage_1.toDraftChangeDisplayItems)(editsSinceOpen);
            previewRisky = editsSinceOpen.some(function (change) { return change.risky; });
            previewSummary = "Unsaved vs server: ".concat(changedDetails);
        }
        else {
            var preview = (0, resultRowDraftStorage_1.buildRestorePreview)(history, target, restoreIndex);
            if (preview === null || preview === void 0 ? void 0 : preview.changes.length) {
                previewTitle = 'Draft changes';
                changeItems = (0, resultRowDraftStorage_1.toDraftChangeDisplayItems)(preview.changes);
                previewRisky = preview.hasRiskyChanges;
            }
            previewSummary = (_c = preview === null || preview === void 0 ? void 0 : preview.summaryText) !== null && _c !== void 0 ? _c : null;
        }
        var existingUi = this.readUi(target);
        this.applyUi(target, {
            savedAt: restoreRevision ? new Date(restoreRevision.savedAt) : null,
            revisionCount: history.revisions.length,
            previewSummary: previewSummary,
            previewTitle: previewTitle,
            changeItems: changeItems,
            previewRisky: previewRisky,
            changedFields: changedLabels.length ? changedLabels.join(', ') : null,
            changedDetails: changedDetails,
            restoreWarning: null,
            clearWarning: existingUi.clearWarning,
            undoAvailable: !!session.undoSnapshot,
            archivedCount: archivedCount,
            archivedItems: archivedItems,
        });
    };
    ResultRowDraftServiceImpl.prototype.readUi = function (target) {
        var _a, _b, _c, _d, _f, _g, _h, _j, _k, _l, _m;
        return {
            savedAt: (_a = target._localDraftSavedAt) !== null && _a !== void 0 ? _a : null,
            revisionCount: (_b = target._localDraftRevisionCount) !== null && _b !== void 0 ? _b : 0,
            previewSummary: (_c = target._draftPreviewSummary) !== null && _c !== void 0 ? _c : null,
            previewTitle: (_d = target._draftPreviewTitle) !== null && _d !== void 0 ? _d : null,
            changeItems: (_f = target._draftChangeItems) !== null && _f !== void 0 ? _f : [],
            previewRisky: !!target._draftPreviewRisky,
            changedFields: (_g = target._draftChangedFields) !== null && _g !== void 0 ? _g : null,
            changedDetails: (_h = target._draftChangedDetails) !== null && _h !== void 0 ? _h : null,
            restoreWarning: (_j = target._draftRestoreWarning) !== null && _j !== void 0 ? _j : null,
            clearWarning: (_k = target._draftClearWarning) !== null && _k !== void 0 ? _k : null,
            undoAvailable: !!target._draftUndoAvailable,
            archivedCount: (_l = target._archivedDraftCount) !== null && _l !== void 0 ? _l : 0,
            archivedItems: (_m = target._draftArchivedItems) !== null && _m !== void 0 ? _m : [],
        };
    };
    ResultRowDraftServiceImpl.prototype.applyUi = function (target, ui) {
        target._localDraftSavedAt = ui.savedAt;
        target._localDraftRevisionCount = ui.revisionCount;
        target._draftPreviewSummary = ui.previewSummary;
        target._draftPreviewTitle = ui.previewTitle;
        target._draftChangeItems = ui.changeItems;
        target._draftPreviewRisky = ui.previewRisky;
        target._draftChangedFields = ui.changedFields;
        target._draftChangedDetails = ui.changedDetails;
        target._draftRestoreWarning = ui.restoreWarning;
        target._draftClearWarning = ui.clearWarning;
        target._draftUndoAvailable = ui.undoAvailable;
        target._archivedDraftCount = ui.archivedCount;
        target._draftArchivedItems = ui.archivedItems;
        // Compact toolbar signal: local draft exists and/or form differs from open baseline.
        target._draftIndicator =
            ui.revisionCount > 0 || !!ui.changedFields || ui.changeItems.length > 0;
        target._draftUnsavedIndicator = !!ui.changedFields || ui.changeItems.length > 0;
        // Never force-open panels from refresh (typing/persist); leave user toggles alone.
        if (target._draftDiffOpen === undefined) {
            target._draftDiffOpen = false;
        }
        if (target._draftArchivedVisible === undefined) {
            target._draftArchivedVisible = false;
        }
    };
    ResultRowDraftServiceImpl.prototype.emptyUi = function () {
        return {
            savedAt: null,
            revisionCount: 0,
            previewSummary: null,
            previewTitle: null,
            changeItems: [],
            previewRisky: false,
            changedFields: null,
            changedDetails: null,
            restoreWarning: null,
            clearWarning: null,
            undoAvailable: false,
            archivedCount: 0,
            archivedItems: [],
        };
    };
    return ResultRowDraftServiceImpl;
}());
exports.ResultRowDraftServiceImpl = ResultRowDraftServiceImpl;
exports.resultRowDraftServiceFactory = [
    '$timeout',
    '$rootScope',
    function ($timeout, $rootScope) {
        var service = new ResultRowDraftServiceImpl($timeout, function () {
            try {
                return window.localStorage;
            }
            catch (_a) {
                return null;
            }
        });
        // Push live body onto parent edit sessions + working stream (bypass isolate lag).
        $rootScope.$on('audDraftApplyLiveBody', function (_e, payload) {
            if (payload && payload.body != null) {
                service.applyLiveBodyToOpenEditSessions(String(payload.body), payload.source || 'unknown', payload.rowId);
            }
        });
        // Continuous typing stream — every input event (not blur-only).
        $rootScope.$on('audTypingStream', function (_e, payload) {
            if (payload && payload.body != null) {
                service.recordTypingForOpenSessions(String(payload.body), payload.source || 'type', !!payload.immediate, payload.rowId);
            }
        });
        $rootScope.$on('audTypingStreamFlush', function (_e, payload) {
            // Always flush pending ticks; row-scoped body applies already require rowId.
            void payload;
            service.flushTypingForOpenSessions();
        });
        // Immediate write: blur, map structure write, save path, tab hide, emergency.
        // rowId scopes persist to one note (outline destroy must not flush another open record).
        $rootScope.$on('audDraftPersistHint', function (_e, payload) {
            if (payload && payload.rowId != null && payload.rowId !== '') {
                service.flushOpenEditSession(payload.rowId);
            }
            else {
                // Global unload only — per-outline emitters must pass rowId.
                service.flushAllOpenSessions();
            }
        });
        // After outline flush (live textarea → model), persist matching open draft(s).
        $rootScope.$on('audOutlineFlush', function (_e, payload) {
            if (payload && payload.rowId != null && payload.rowId !== '') {
                service.flushOpenEditSession(payload.rowId);
            }
            else {
                service.flushAllOpenSessions();
            }
        });
        // Debounced write: outline/textarea keystrokes (open → type → act).
        $rootScope.$on('audDraftSchedulePersist', function (_e, payload) {
            service.scheduleDebouncedPersistAll(payload && payload.rowId != null && payload.rowId !== ''
                ? payload.rowId
                : undefined);
        });
        // Navigation emergency: outline pulls live map/text, then we persist.
        $rootScope.$on('audDraftEmergencyPersist', function (_e, payload) {
            if (payload && payload.rowId != null && payload.rowId !== '') {
                service.flushOpenEditSession(payload.rowId);
            }
            else {
                service.flushAllOpenSessions();
            }
        });
        return service;
    },
];
